import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, catchError, tap, throwError } from 'rxjs';
import { NotificationService } from '@/app/shared/services/notification.service';
import { environment } from '@/environments/environment';

type ApiResponseBody = {
  isSuccess?: boolean;
  statusCode?: number;
  message?: string;
};

@Injectable()
export class ApiMessageInterceptor implements HttpInterceptor {
  constructor(private notificationService: NotificationService) { }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (!this.isApiRequest(req.url)) {
      return next.handle(req);
    }

    return next.handle(req).pipe(
      tap((event) => {
        if (!(event instanceof HttpResponse)) {
          return;
        }

        const responseBody = this.toApiResponseBody(event.body);
        if (!responseBody) {
          return;
        }

        const message = this.normalizeMessage(responseBody.message);
        if (!message) {
          return;
        }

        if (req.method === 'GET' && this.isSuccessfulResponse(responseBody)) {
          return;
        }

        this.notificationService.apiResponse(
          this.isSuccessfulResponse(responseBody) ? 'success' : 'error',
          message,
        );
      }),
      catchError((error: HttpErrorResponse) => {
        const message = this.extractErrorMessage(error);

        if (message) {
          this.notificationService.apiResponse('error', message);
        }

        return throwError(() => error);
      })
    );
  }

  private isApiRequest(url: string): boolean {
    return !!url && url.startsWith(environment.baseURL);
  }

  private toApiResponseBody(body: unknown): ApiResponseBody | null {
    if (!body || typeof body !== 'object' || Array.isArray(body)) {
      return null;
    }

    const candidate = body as Record<string, unknown>;
    const hasMessage = typeof candidate['message'] === 'string';
    const hasIsSuccess = typeof candidate['isSuccess'] === 'boolean';
    const hasStatusCode = typeof candidate['statusCode'] === 'number';

    return hasMessage && (hasIsSuccess || hasStatusCode)
      ? candidate as ApiResponseBody
      : null;
  }

  private isSuccessfulResponse(body: ApiResponseBody): boolean {
    if (typeof body.isSuccess === 'boolean') {
      return body.isSuccess;
    }

    if (typeof body.statusCode === 'number') {
      return body.statusCode >= 200 && body.statusCode < 300;
    }

    return false;
  }

  private extractErrorMessage(error: HttpErrorResponse): string | null {
    const errorBody = error.error;

    if (typeof errorBody === 'string') {
      return this.normalizeMessage(errorBody);
    }

    if (errorBody && typeof errorBody === 'object' && !Array.isArray(errorBody)) {
      const errorRecord = errorBody as Record<string, unknown>;

      return this.normalizeMessage(errorRecord['message'])
        ?? this.normalizeMessage(errorRecord['Message'])
        ?? this.normalizeMessage(errorRecord['title'])
        ?? this.normalizeMessage(errorRecord['Title']);
    }

    return this.normalizeMessage(error.message);
  }

  private normalizeMessage(value: unknown): string | null {
    return typeof value === 'string' && value.trim()
      ? value.trim()
      : null;
  }
}